import { effect } from '@preact/signals';
import { DoubleSide, Group, MathUtils, Mesh, MeshBasicMaterial, PlaneGeometry, type Texture, Vector3 } from 'three';
import type { Article, ClientView, KickerId, Locale, PublicPlayer } from '@gazette/shared';
import { sfx } from '../audio/sfx.ts';
import { CLIPPING_WIDTH, FICHE_SIZE, drawClipping, drawFiche, drawStamp, type StampWord } from '../art/cards.ts';
import { renderGazette } from '../art/gazette.ts';
import { loadCanvasFonts } from '../art/text.ts';
import { locale, t } from '../i18n/index.ts';
import { view } from '../net/client.ts';
import { Engine, type Box, type ScreenRect } from './engine.ts';
import { CardItem, Thread, canvasTexture, decal, rubberStamp } from './items.ts';
import { feltMaterial } from './materials.ts';
import { ease } from './tween.ts';

const COLUMN = 2.45;
const FICHE_W = 1.92;
const FICHE_H = (FICHE_W * FICHE_SIZE.height) / FICHE_SIZE.width;
const CLIP_W = 1.96;
const CLIP_GAP = 0.32;
const FIRST_CLIP_TOP = -FICHE_H / 2 - 0.5;
const ROW_GAP = 0.9;

interface Slot {
  x: number;
  y: number;
  rotation: number;
}

interface Art {
  texture: Texture;
  aspect: number;
}

interface Layout {
  fiches: Map<string, Slot>;
  clips: Map<string, Slot & { h: number }>;
  box: Box;
}

function hash(key: string): number {
  let h = 2166136261;
  for (let i = 0; i < key.length; i++) h = Math.imul(h ^ key.charCodeAt(i), 16777619);
  return (h >>> 0) / 4294967296;
}

/** Cards are pinned by hand: a little off-grid, never twice the same. */
function jitter(key: string): { dx: number; dy: number; rotation: number } {
  return { dx: (hash(`${key}x`) - 0.5) * 0.12, dy: (hash(`${key}y`) - 0.5) * 0.1, rotation: (hash(`${key}r`) - 0.5) * 0.09 };
}

function union(boxes: Box[]): Box {
  const valid = boxes.filter(Boolean);
  if (valid.length === 0) return { x: 0, y: 0, w: 4, h: 4 };
  const left = Math.min(...valid.map((b) => b.x - b.w / 2));
  const right = Math.max(...valid.map((b) => b.x + b.w / 2));
  const bottom = Math.min(...valid.map((b) => b.y - b.h / 2));
  const top = Math.max(...valid.map((b) => b.y + b.h / 2));
  return { x: (left + right) / 2, y: (bottom + top) / 2, w: right - left, h: top - bottom };
}

/** The part of the screen not covered by panels marked `data-occlude`. */
function freeArea(): ScreenRect {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  let top = 0;
  let bottom = vh;
  let left = 0;
  let right = vw;
  for (const element of document.querySelectorAll<HTMLElement>('[data-occlude]')) {
    const rect = element.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) continue;
    if (rect.width > vw * 0.7) {
      if (rect.top + rect.height / 2 < vh / 2) top = Math.max(top, rect.bottom);
      else bottom = Math.min(bottom, Math.max(rect.top, 0));
    } else if (rect.left + rect.width / 2 > vw / 2) {
      right = Math.min(right, rect.left);
    } else {
      left = Math.max(left, rect.right);
    }
  }
  if (bottom - top < vh * 0.18) {
    top = 0;
    bottom = vh * 0.34;
  }
  return { x: left, y: top, width: right - left, height: bottom - top };
}

const SAMPLE = [
  { name: 'Thomas', portrait: 1203 },
  { name: 'Léa', portrait: 5417 },
  { name: 'Inès', portrait: 8821 },
  { name: 'Hugo', portrait: 3310 },
];

/**
 * Keeps the 3D investigation board in step with the game: one fiche per suspect, each
 * published article pinned under its victim, and the two set pieces, the publication and
 * the reveal, played once when their phase begins.
 */
export class BoardDirector {
  private readonly engine: Engine;
  private readonly board = new Group();
  private readonly fiches = new Map<string, CardItem>();
  private readonly ficheKeys = new Map<string, string>();
  private readonly clippings = new Map<string, CardItem>();
  private readonly clippingKeys = new Map<string, string>();
  private readonly stampDecals = new Map<string, Mesh>();
  private readonly stampArt = new Map<string, Art>();
  private readonly clipAspect = new Map<string, number>();
  private readonly plumeMarks = new Set<string>();
  private transient: Thread[] = [];
  private accusation: Thread | null = null;
  private authorMark: { item: CardItem; mark: Mesh } | null = null;
  private sample: { lang: Locale; items: CardItem[]; thread: Thread; box: Box } | null = null;
  private revealing: string | null = null;
  private epoch = 0;
  private sceneKey = '';
  private game = '';
  private lastBox: Box | null = null;
  private lastFree = '';
  private readonly reduced = matchMedia('(prefers-reduced-motion: reduce)');

  constructor(canvas: HTMLCanvasElement) {
    this.engine = new Engine(canvas);
    const felt = new Mesh(new PlaneGeometry(400, 300), feltMaterial(this.engine.pixelRatio));
    this.engine.scene.add(felt, this.board);
  }

  async start(): Promise<void> {
    await loadCanvasFonts();
    effect(() => this.sync(view.value, locale.value));
    let resizeTimer = 0;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(() => {
        this.sync(view.value, locale.value);
        this.reframe(0);
      }, 120);
    });
  }

  // Panels grow and shrink with their content; keep the framed subject in the free area.
  private readonly occluderObserver = new ResizeObserver(() => this.reframeIfFreeAreaMoved());

  private watchOccluders(): void {
    this.occluderObserver.disconnect();
    for (const element of document.querySelectorAll('[data-occlude]')) this.occluderObserver.observe(element);
    this.reframeIfFreeAreaMoved();
  }

  private get motion(): boolean {
    return !this.reduced.matches;
  }

  private ms(duration: number): number {
    return this.motion ? duration : 0;
  }

  // ------------------------------------------------------------------ sync

  private sync(current: ClientView | null, lang: Locale): void {
    const phase = current?.phase;
    const article = phase && 'articleId' in phase ? phase.articleId : '';
    const key = current ? `${current.code}:${current.round}:${phase!.name}:${article}` : `home:${lang}`;
    const entering = key !== this.sceneKey;
    if (entering) {
      this.sceneKey = key;
      this.epoch += 1;
      this.engine.animator.finishAll();
    }

    if (!current) {
      this.clearGame();
      this.showHome(lang, entering);
      return;
    }
    this.hideHome();
    const game = `${current.code}:${current.round}`;
    if (game !== this.game) {
      this.clearGame();
      this.game = game;
    }
    if (!current.gazette) this.clearClippings();

    this.syncFiches(current, lang);
    if (current.gazette && current.phase.name !== 'publication') this.syncClippings(current, lang);
    this.syncStamps(current, lang);
    if (entering) this.enterPhase(current, lang);
    else if (current.phase.name === 'investigation') this.syncAccusation(current, true);
    else if (current.phase.name === 'lobby') this.frame(this.layout(current).box, 500);
    requestAnimationFrame(() => this.watchOccluders());
  }

  private enterPhase(current: ClientView, lang: Locale): void {
    const epoch = this.epoch;
    switch (current.phase.name) {
      case 'lobby':
      case 'confessions':
      case 'writing':
        this.clearTransient();
        void this.frame(this.layout(current).box, 800);
        return;
      case 'publication':
        this.clearTransient();
        void this.publish(current, lang, epoch);
        return;
      case 'investigation':
        this.clearTransient();
        this.syncAccusation(current, false);
        return;
      case 'reveal':
        void this.reveal(current, lang, epoch);
        return;
      case 'plume':
      case 'final':
        this.clearTransient();
        this.focus(null);
        if (current.phase.name === 'final') this.markPlume(current, lang);
        void this.frame(this.layout(current).box, 1000);
        return;
    }
  }

  // --------------------------------------------------------------- fiches

  private ficheSlot(index: number, count: number, id: string): Slot {
    const j = jitter(id);
    return { x: (index - (count - 1) / 2) * COLUMN + j.dx, y: j.dy, rotation: j.rotation };
  }

  /**
   * Suspects sit in rows sized to the device, so a phone held upright gets a grid instead of
   * one thin strip across empty felt. Each row leaves room below its fiches for the clippings
   * pinned under them.
   */
  private layout(current: ClientView): Layout {
    const players = current.players;
    const stacks = new Map<string, { id: string; h: number }[]>();
    for (const article of current.gazette?.articles ?? []) {
      const aspect = this.clipAspect.get(article.id);
      if (!aspect) continue;
      const stack = stacks.get(article.targetId) ?? [];
      stack.push({ id: article.id, h: CLIP_W / aspect });
      stacks.set(article.targetId, stack);
    }
    const depth = (id: string) => {
      const stack = stacks.get(id) ?? [];
      return stack.length ? 0.5 + stack.reduce((sum, clip) => sum + clip.h, 0) + CLIP_GAP * (stack.length - 1) : 0;
    };

    const perRow = this.columnsPerRow(
      players.map((player) => player.id),
      depth,
    );
    const fiches = new Map<string, Slot>();
    const clips = new Map<string, Slot & { h: number }>();
    let rowY = 0;
    for (let start = 0; start < players.length; start += perRow) {
      const row = players.slice(start, start + perRow);
      row.forEach((player, column) => {
        const x = (column - (row.length - 1) / 2) * COLUMN;
        const j = jitter(player.id);
        fiches.set(player.id, { x: x + j.dx, y: rowY + j.dy, rotation: j.rotation });
        let top = rowY + FIRST_CLIP_TOP;
        for (const clip of stacks.get(player.id) ?? []) {
          const c = jitter(clip.id);
          clips.set(clip.id, { x: x + c.dx, y: top - clip.h / 2, rotation: c.rotation * 1.4, h: clip.h });
          top -= clip.h + CLIP_GAP;
        }
      });
      rowY -= FICHE_H + Math.max(0, ...row.map((player) => depth(player.id))) + ROW_GAP;
    }

    const boxes: Box[] = [...fiches.values()].map((slot) => ({ x: slot.x, y: slot.y, w: FICHE_W, h: FICHE_H }));
    for (const slot of clips.values()) boxes.push({ x: slot.x, y: slot.y, w: CLIP_W, h: slot.h });
    const box = union(boxes);
    // Never framed tighter than two columns: one suspect alone should not fill the screen.
    return { fiches, clips, box: { ...box, w: Math.max(box.w + 0.5, 2 * COLUMN), h: box.h + 0.5 } };
  }

  /** The row length that shows the whole board biggest in the room the panels usually leave. */
  private columnsPerRow(ids: string[], depth: (id: string) => number): number {
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const free = vw >= 960 ? { width: vw - Math.min(480, vw * 0.44), height: vh - 56 } : { width: vw, height: vh * 0.55 };
    let best = Math.max(1, ids.length);
    let bestScale = 0;
    for (let perRow = best; perRow >= 1; perRow--) {
      let height = 0;
      for (let start = 0; start < ids.length; start += perRow) {
        height += FICHE_H + Math.max(0, ...ids.slice(start, start + perRow).map(depth)) + ROW_GAP;
      }
      const width = Math.max(2, Math.min(perRow, ids.length)) * COLUMN;
      const scale = Math.min(free.width / width, free.height / height);
      // Fewer rows win ties: a row only breaks when the board gets clearly bigger.
      if (scale > bestScale * 1.05) {
        best = perRow;
        bestScale = scale;
      }
    }
    return best;
  }

  private syncFiches(current: ClientView, lang: Locale): void {
    const players = current.players;
    const present = new Set(players.map((player) => player.id));
    for (const [id, item] of this.fiches) {
      if (present.has(id)) continue;
      this.fiches.delete(id);
      this.ficheKeys.delete(id);
      void this.engine.animator
        .tween(this.ms(260), (t) => {
          item.setOpacity(1 - t);
          item.setLift(t * 0.6);
        })
        .then(() => item.dispose());
    }

    const layout = this.layout(current);
    players.forEach((player, index) => {
      const slot = layout.fiches.get(player.id)!;
      const key = `${player.name}|${player.portrait}|${index}|${lang}|${current.code}`;
      let item = this.fiches.get(player.id);
      if (!item) {
        item = this.makeFiche(player, index, current.code, lang);
        this.fiches.set(player.id, item);
        this.ficheKeys.set(player.id, key);
        this.board.add(item.group);
        item.group.position.set(slot.x, slot.y, 0);
        item.group.rotation.z = slot.rotation;
        if (current.phase.name === 'lobby') this.dropIn(item, 0);
      } else if (this.ficheKeys.get(player.id) !== key) {
        this.ficheKeys.set(player.id, key);
        const art = drawFiche({ name: player.name, portrait: player.portrait, number: index + 1, code: current.code, locale: lang });
        item.setSource({ texture: canvasTexture(art.canvas), width: FICHE_W, aspect: art.width / art.height }, true);
      }
      item.setDim(player.online ? 1 : 0.6);
      this.moveTo(item, slot, 420);
    });
  }

  private makeFiche(player: Pick<PublicPlayer, 'name' | 'portrait'>, index: number, code: string | null, lang: Locale): CardItem {
    const art = drawFiche({ name: player.name, portrait: player.portrait, number: index + 1, code, locale: lang });
    return new CardItem({ texture: canvasTexture(art.canvas), width: FICHE_W, aspect: art.width / art.height }, this.engine.pixelRatio, true);
  }

  private moveTo(item: CardItem, slot: Slot, duration: number): void {
    const from = item.group.position.clone();
    const fromRotation = item.group.rotation.z;
    if (from.distanceTo(new Vector3(slot.x, slot.y, 0)) < 1e-3 && Math.abs(fromRotation - slot.rotation) < 1e-3) return;
    void this.engine.animator.tween(this.ms(duration), (t) => {
      item.group.position.set(MathUtils.lerp(from.x, slot.x, t), MathUtils.lerp(from.y, slot.y, t), 0);
      item.group.rotation.z = MathUtils.lerp(fromRotation, slot.rotation, t);
    });
  }

  /** Card arrives from above the board and is pushed onto its pin. */
  private dropIn(item: CardItem, delay: number): Promise<void> {
    if (!this.motion) return Promise.resolve();
    item.setLift(1.4);
    item.setOpacity(0);
    item.showPin(false);
    return this.engine.animator
      .tween(
        420,
        (t) => {
          item.setOpacity(Math.min(1, t * 3));
          item.setLift(1.4 * (1 - t));
        },
        { ease: ease.outBack, delay },
      )
      .then(() => {
        item.setLift(0);
        item.showPin(true);
        sfx.pin();
      });
  }

  // ------------------------------------------------------------ clippings

  private clippingArt(article: Article, lang: Locale): Art {
    const art = drawClipping({ ...article, locale: lang, seed: hash(article.id) * 1e6 });
    this.clipAspect.set(article.id, art.width / art.height);
    return { texture: canvasTexture(art.canvas), aspect: art.width / art.height };
  }

  private syncClippings(current: ClientView, lang: Locale): void {
    const created = new Set<string>();
    for (const article of current.gazette!.articles) {
      const key = `${article.id}|${lang}`;
      const existing = this.clippings.get(article.id);
      if (existing && this.clippingKeys.get(article.id) === key) continue;
      const art = this.clippingArt(article, lang);
      this.clippingKeys.set(article.id, key);
      if (existing) {
        existing.setSource({ texture: art.texture, width: CLIP_W, aspect: art.aspect }, true);
      } else {
        const item = new CardItem({ texture: art.texture, width: CLIP_W, aspect: art.aspect }, this.engine.pixelRatio, true);
        this.clippings.set(article.id, item);
        this.board.add(item.group);
        created.add(article.id);
      }
    }
    for (const [id, slot] of this.layout(current).clips) {
      const item = this.clippings.get(id);
      if (!item) continue;
      if (created.has(id)) {
        item.group.position.set(slot.x, slot.y, 0);
        item.group.rotation.z = slot.rotation;
      } else {
        this.moveTo(item, slot, 420);
      }
    }
  }

  private clearClippings(): void {
    for (const item of this.clippings.values()) item.dispose();
    this.clippings.clear();
    this.clippingKeys.clear();
    this.stampDecals.clear();
    this.clipAspect.clear();
    this.plumeMarks.clear();
  }

  // --------------------------------------------------------------- stamps

  private stampTexture(word: StampWord, lang: Locale): Art {
    const key = `${word}|${lang}`;
    let art = this.stampArt.get(key);
    if (!art) {
      const drawn = drawStamp(word, lang, word.length * 17);
      art = { texture: canvasTexture(drawn.canvas), aspect: drawn.width / drawn.height };
      this.stampArt.set(key, art);
    }
    return art;
  }

  private inkOnto(item: CardItem, word: StampWord, lang: Locale, seedKey: string): Mesh {
    const art = this.stampTexture(word, lang);
    const mark = decal(art.texture, item.width * (word === 'plume' ? 0.62 : 0.8), art.aspect);
    mark.rotation.z = (hash(`${seedKey}s`) - 0.5) * 0.4;
    const y = word === 'plume' ? item.height * 0.3 : -item.height * 0.06;
    mark.position.set((hash(`${seedKey}sx`) - 0.5) * item.width * 0.12, y, 0.006);
    item.body.add(mark);
    return mark;
  }

  private syncStamps(current: ClientView, lang: Locale): void {
    for (const verdict of current.verdicts) {
      if (this.stampDecals.has(verdict.articleId) || verdict.articleId === this.revealing) continue;
      const item = this.clippings.get(verdict.articleId);
      if (!item) continue;
      const pending = current.phase.name === 'reveal' && current.phase.articleId === verdict.articleId && this.sceneKey.endsWith(verdict.articleId);
      if (pending && this.motion) continue;
      this.stampDecals.set(verdict.articleId, this.inkOnto(item, verdict.isTrue ? 'true' : 'false', lang, verdict.articleId));
    }
  }

  /** The rubber stamp drops, inks the card, and lifts away. */
  private async stampDown(item: CardItem, word: StampWord, lang: Locale, seedKey: string): Promise<Mesh> {
    if (!this.motion) {
      sfx.stamp();
      return this.inkOnto(item, word, lang, seedKey);
    }
    const stamp = rubberStamp();
    stamp.scale.setScalar(item.width * 0.6);
    const base = item.group.position;
    stamp.position.set(base.x + 0.1, base.y - item.height * 0.06, 6);
    stamp.rotation.z = item.group.rotation.z + (hash(`${seedKey}s`) - 0.5) * 0.4;
    this.board.add(stamp);
    const contact = item.lift + 0.16;
    await this.engine.animator.tween(240, (t) => (stamp.position.z = MathUtils.lerp(6, contact, t)), { ease: ease.inCubic });
    const mark = this.inkOnto(item, word, lang, seedKey);
    this.engine.shake(0.09);
    sfx.stamp();
    await this.engine.animator.tween(380, (t) => (stamp.position.z = MathUtils.lerp(contact, 7, t)), { ease: ease.inCubic, delay: 140 });
    stamp.removeFromParent();
    stamp.traverse((child) => {
      if (child instanceof Mesh) {
        child.geometry.dispose();
        (child.material as MeshBasicMaterial).dispose();
      }
    });
    return mark;
  }

  /** The Golden Quill winners get a brass stamp on the board, where the whole group can see it. */
  private markPlume(current: ClientView, lang: Locale): void {
    if (current.phase.name !== 'final') return;
    for (const id of current.phase.results.plume.articleIds) {
      const item = this.clippings.get(id);
      if (!item || this.plumeMarks.has(id)) continue;
      this.plumeMarks.add(id);
      this.inkOnto(item, 'plume', lang, `${id}p`);
    }
  }

  // ---------------------------------------------------------- set pieces

  private async publish(current: ClientView, lang: Locale, epoch: number): Promise<void> {
    const gazette = current.gazette!;
    this.clearClippings();
    const arts = new Map(gazette.articles.map((article) => [article.id, this.clippingArt(article, lang)]));
    const layout = this.layout(current);
    const slots = layout.clips;
    // Rows open up to make room for the clippings about to be pinned under each fiche.
    for (const [id, slot] of layout.fiches) {
      const fiche = this.fiches.get(id);
      if (fiche) this.moveTo(fiche, slot, 700);
    }
    const place = () => {
      for (const article of gazette.articles) {
        const art = arts.get(article.id)!;
        const item = new CardItem({ texture: art.texture, width: CLIP_W, aspect: art.aspect }, this.engine.pixelRatio, true);
        const slot = slots.get(article.id)!;
        item.group.position.set(slot.x, slot.y, 0);
        item.group.rotation.z = slot.rotation;
        this.clippings.set(article.id, item);
        this.clippingKeys.set(article.id, `${article.id}|${lang}`);
        this.board.add(item.group);
      }
    };
    const whole = () => layout.box;

    if (!this.motion) {
      place();
      void this.frame(whole(), 0);
      return;
    }

    const render = renderGazette({ gazette, players: current.players, locale: lang, verdicts: [], results: null });
    const texture = canvasTexture(render.canvas);
    const width = 6.4;
    const height = width / render.aspect;
    const target = whole();
    const center = new Vector3(target.x, Math.min(target.y, -1), 0.7);
    const paper = new Mesh(new PlaneGeometry(width, height), new MeshBasicMaterial({ map: texture, side: DoubleSide, transparent: true }));
    paper.position.set(center.x, center.y, 34);
    this.board.add(paper);
    const cleanup = () => {
      paper.removeFromParent();
      paper.geometry.dispose();
      paper.material.dispose();
    };

    await this.frame({ x: center.x, y: center.y, w: width, h: height }, 450);
    if (epoch !== this.epoch) return cleanup();
    sfx.press();
    sfx.swoosh(1.3);
    await this.engine.animator.tween(
      1600,
      (t) => {
        paper.position.z = MathUtils.lerp(34, center.z, t);
        paper.rotation.z = (1 - t) * Math.PI * 7 - 0.04 * t;
      },
      { ease: ease.outCubic },
    );
    this.engine.shake(0.04);
    sfx.stamp();
    await this.engine.animator.wait(1300);
    if (epoch !== this.epoch) return cleanup();

    // Scissors: every article lifts out of the page and flies to its victim's column.
    const flights = gazette.articles.map((article, index) => {
      const rect = render.rects.get(article.id)!;
      const w = rect.w * width;
      const h = rect.h * height;
      const x = center.x - width / 2 + (rect.x + rect.w / 2) * width;
      const y = center.y + height / 2 - (rect.y + rect.h / 2) * height;
      const item = new CardItem({ texture, width: w, aspect: w / h, region: rect }, this.engine.pixelRatio);
      item.group.position.set(x, y, 0);
      item.group.rotation.z = paper.rotation.z;
      item.setLift(center.z + 0.02);
      item.showPin(false);
      this.board.add(item.group);
      this.clippings.set(article.id, item);
      this.clippingKeys.set(article.id, `${article.id}|${lang}`);
      return this.fly(item, slots.get(article.id)!, arts.get(article.id)!, 250 + index * 120, center.z);
    });
    paper.material.opacity = 1;
    void this.engine.animator
      .tween(
        900,
        (t) => {
          paper.position.y = center.y - t * height * 1.5;
          paper.rotation.z = -0.04 - t * 0.6;
          paper.material.opacity = 1 - t;
        },
        { ease: ease.inCubic, delay: 200 },
      )
      .then(cleanup);
    void this.frame(target, 1500);
    sfx.swoosh(0.8);
    await Promise.all(flights);
    texture.dispose();
  }

  private async fly(item: CardItem, slot: Slot & { h: number }, art: Art, delay: number, startLift: number): Promise<void> {
    const from = item.group.position.clone();
    const fromRotation = item.group.rotation.z;
    const fromW = item.width;
    const fromH = item.height;
    let swapped = false;
    await this.engine.animator.tween(
      1050,
      (t) => {
        item.group.position.set(MathUtils.lerp(from.x, slot.x, t), MathUtils.lerp(from.y, slot.y, t), 0);
        item.group.rotation.z = MathUtils.lerp(fromRotation, slot.rotation, t) + Math.sin(t * Math.PI) * 0.3;
        item.setLift(MathUtils.lerp(startLift, 0, t) + Math.sin(t * Math.PI) * 1.2);
        const w = MathUtils.lerp(fromW, CLIP_W, t);
        const h = MathUtils.lerp(fromH, slot.h, t);
        if (!swapped && t > 0.45) {
          swapped = true;
          item.setSource({ texture: art.texture, width: w, aspect: w / h }, true);
        }
        item.setSize(w, h);
      },
      { ease: ease.inOutCubic, delay },
    );
    if (!swapped) item.setSource({ texture: art.texture, width: CLIP_W, aspect: art.aspect }, true);
    item.setLift(0);
    item.showPin(true);
    sfx.pin();
  }

  /** Lifts the case under investigation and pushes the others back. */
  private focus(articleId: string | null): void {
    for (const [id, item] of this.clippings) {
      const active = id === articleId;
      item.setDim(articleId === null || active ? 1 : 0.5);
      const from = item.lift;
      const to = active ? 0.14 : 0;
      if (Math.abs(from - to) > 1e-3) void this.engine.animator.tween(this.ms(300), (t) => item.setLift(MathUtils.lerp(from, to, t)));
    }
  }

  private threadBetween(from: CardItem, to: CardItem, suspectId: string, offset = 0): Thread {
    const thread = new Thread(suspectId);
    const start = from.pinWorldPosition().add(new Vector3(offset, 0, 0));
    const end = to.pinWorldPosition();
    thread.set(start, end, start.distanceTo(end) * 0.12, 0.001);
    this.board.add(thread.mesh);
    return thread;
  }

  private drawThread(thread: Thread, delay = 0): Promise<void> {
    sfx.pluck(delay / 1000);
    return this.engine.animator.tween(this.ms(420), (t) => thread.update({ progress: t }), { ease: ease.outCubic, delay: this.ms(delay) });
  }

  /** A wrong accusation lets go of the clipping and falls off the board, clear of the verdict. */
  private dropThread(thread: Thread): Promise<void> {
    const start = thread.from.clone();
    const fallen = start.clone().add(new Vector3(0.4, -5, 0.3));
    const sag = thread.sag;
    return this.engine.animator
      .tween(
        this.ms(720),
        (t) => {
          thread.update({ from: start.clone().lerp(fallen, t), sag: MathUtils.lerp(sag, 0.02, t) });
          thread.setOpacity(1 - Math.max(0, (t - 0.35) / 0.65));
        },
        { ease: ease.inCubic },
      )
      .then(() => {
        thread.mesh.visible = false;
        this.engine.invalidate();
      });
  }

  /** The viewer's own accusation during the investigation; nobody else sees it. */
  private syncAccusation(current: ClientView, animate: boolean): void {
    if (current.phase.name !== 'investigation') return;
    const phase = current.phase;
    const clip = this.clippings.get(phase.articleId);
    const victim = this.fiches.get(current.gazette?.articles.find((article) => article.id === phase.articleId)?.targetId ?? '');
    this.focus(phase.articleId);
    const guess = phase.role === 'victim' ? null : phase.vote.authorGuess;
    if (this.accusation?.suspectId !== (guess ?? undefined)) {
      const previous = this.accusation;
      this.accusation = null;
      if (previous) void this.dropThread(previous).then(() => previous.dispose());
      const suspect = guess ? this.fiches.get(guess) : undefined;
      if (clip && suspect && guess) {
        this.accusation = this.threadBetween(clip, suspect, guess);
        void this.drawThread(this.accusation);
      }
    }
    const suspect = guess ? this.fiches.get(guess) : undefined;
    const boxes = [clip?.bounds(), victim?.bounds(), suspect?.bounds()].filter((box): box is Box => !!box);
    void this.frame(union(boxes), animate ? 600 : 700);
  }

  private async reveal(current: ClientView, lang: Locale, epoch: number): Promise<void> {
    if (current.phase.name !== 'reveal') return;
    const verdict = current.phase.verdict;
    this.clearTransient();
    const clip = this.clippings.get(verdict.articleId);
    if (!clip) return;
    const victim = this.fiches.get(verdict.victimId);
    const author = this.fiches.get(verdict.authorId);
    this.focus(verdict.articleId);
    const alreadyInked = this.stampDecals.has(verdict.articleId);
    this.revealing = alreadyInked ? null : verdict.articleId;

    await this.frame(union([clip.bounds(), victim?.bounds() ?? clip.bounds()]), 450);
    if (epoch !== this.epoch) return;
    if (!alreadyInked) {
      const mark = await this.stampDown(clip, verdict.isTrue ? 'true' : 'false', lang, verdict.articleId);
      this.stampDecals.set(verdict.articleId, mark);
      this.revealing = null;
    }
    if (epoch !== this.epoch) return;
    await this.engine.animator.wait(this.ms(450));

    const accusations = verdict.votes.filter((vote) => vote.authorGuess && this.fiches.has(vote.authorGuess));
    const threads = accusations.map((vote, index) =>
      this.threadBetween(clip, this.fiches.get(vote.authorGuess!)!, vote.authorGuess!, (index - (accusations.length - 1) / 2) * 0.05),
    );
    this.transient.push(...threads);
    threads.forEach((thread, index) => void this.drawThread(thread, index * 90));
    const accused = accusations.map((vote) => this.fiches.get(vote.authorGuess!)!.bounds());
    await this.frame(union([clip.bounds(), ...(victim ? [victim.bounds()] : []), ...accused, ...(author ? [author.bounds()] : [])]), 700);
    if (epoch !== this.epoch) return;
    sfx.drumroll(1);
    await this.engine.animator.wait(this.ms(1000));
    if (epoch !== this.epoch || !author) return;

    await this.engine.animator.tween(this.ms(760), (t) => {
      author.setLift(Math.sin(t * Math.PI) * 0.9);
      author.body.rotation.y = t * Math.PI * 2;
    }, { ease: ease.inOutCubic });
    author.body.rotation.y = 0;
    if (epoch !== this.epoch) return;
    const mark = await this.stampDown(author, 'author', lang, `${verdict.articleId}a`);
    this.authorMark = { item: author, mark };
    for (const thread of threads) {
      if (thread.suspectId === verdict.authorId) {
        const sag = thread.sag;
        void this.engine.animator.tween(this.ms(380), (t) => thread.update({ sag: MathUtils.lerp(sag, 0.01, t) }), { ease: ease.outBack });
      } else {
        void this.dropThread(thread);
      }
    }
    if (threads.length) sfx.pluck(0.05);
  }

  private clearTransient(): void {
    for (const thread of this.transient) thread.dispose();
    this.transient = [];
    this.accusation?.dispose();
    this.accusation = null;
    if (this.authorMark) {
      this.authorMark.mark.removeFromParent();
      this.authorMark.mark.geometry.dispose();
      (this.authorMark.mark.material as MeshBasicMaterial).dispose();
      this.authorMark.item.body.rotation.y = 0;
      this.authorMark.item.setLift(0);
      this.authorMark = null;
    }
  }

  private clearGame(): void {
    this.clearTransient();
    this.clearClippings();
    for (const item of this.fiches.values()) item.dispose();
    this.fiches.clear();
    this.ficheKeys.clear();
    this.game = '';
  }

  // ------------------------------------------------------------------ home

  private showHome(lang: Locale, entering: boolean): void {
    if (this.sample?.lang === lang) {
      if (entering) void this.frame(this.sample.box, 600);
      return;
    }
    const firstVisit = !this.sample;
    this.hideHome();
    const items = SAMPLE.map((suspect, index) => {
      const item = this.makeFiche(suspect, index, null, lang);
      const slot = this.ficheSlot(index, SAMPLE.length, suspect.name);
      item.group.position.set(slot.x, slot.y, 0);
      item.group.rotation.z = slot.rotation;
      this.board.add(item.group);
      return item;
    });
    const sampleArticle: Article = {
      id: 'sample',
      number: 1,
      kicker: 'exclusive' satisfies KickerId,
      headline: t('home.sample.headline', undefined, lang),
      subtitle: t('home.sample.subtitle', undefined, lang),
      targetId: 'Thomas',
    };
    const art = this.clippingArt(sampleArticle, lang);
    const clip = new CardItem({ texture: art.texture, width: CLIP_W, aspect: art.aspect }, this.engine.pixelRatio, true);
    const column = this.ficheSlot(0, SAMPLE.length, 'Thomas');
    clip.group.position.set(column.x + 0.05, FIRST_CLIP_TOP - CLIP_W / art.aspect / 2, 0);
    clip.group.rotation.z = -0.04;
    this.board.add(clip.group);
    items.push(clip);

    const thread = this.threadBetween(clip, items[1]!, 'Léa');
    thread.update({ progress: 1 });
    const box = union(items.map((item) => item.bounds()));
    this.sample = { lang, items, thread, box };
    void this.frame(box, 0);

    if (firstVisit && this.motion) {
      thread.update({ progress: 0.001 });
      void Promise.all(items.map((item, index) => this.dropIn(item, 250 + index * 140))).then(() => this.drawThread(thread));
    }
  }

  private hideHome(): void {
    if (!this.sample) return;
    for (const item of this.sample.items) item.dispose();
    this.sample.thread.dispose();
    this.sample = null;
  }

  // ---------------------------------------------------------------- camera

  private frame(box: Box, duration: number): Promise<void> {
    this.lastBox = box;
    const free = freeArea();
    this.lastFree = `${Math.round(free.x)}:${Math.round(free.y)}:${Math.round(free.width)}:${Math.round(free.height)}`;
    return this.engine.moveCamera(this.engine.framing(box, free), this.ms(duration));
  }

  private reframe(duration: number): void {
    if (this.lastBox) void this.frame(this.lastBox, duration);
  }

  private reframeIfFreeAreaMoved(): void {
    const free = freeArea();
    const key = `${Math.round(free.x)}:${Math.round(free.y)}:${Math.round(free.width)}:${Math.round(free.height)}`;
    if (key !== this.lastFree) this.reframe(350);
  }
}
